/**
 * @file        src/lib/actionTargets.js
 * @description Where each open item is finished, and what the next step is
 *              CALLED. One table, because the Dashboard panel and the Action
 *              Centre page both link into the same twelve screens and a second
 *              copy would drift the moment one of those screens moved.
 *
 *              THE ROUTE KNOWLEDGE LIVES HERE, NOT IN THE API. The backend
 *              returns a `kind` and the record's id; it does not know this
 *              console's URLs, and teaching it would put the admin frontend's
 *              routing table inside an Express service.
 *
 *              THE LINK RESPECTS THE B2C / CORPORATE SPLIT (§9d). A corporate
 *              refund opens on the Corporate Booking List, a retail one on the
 *              B2C list — never the other way round. The item's own
 *              `customerType` decides, so a queue that holds both lands each row
 *              on the screen that owns it.
 *
 *              The BUTTON LABEL is the next real step, not "View". "Approve or
 *              decline", "Settle due", "Upload policy" — a queue is worked
 *              faster when the row says what it is asking for. The two queues
 *              that can be closed in place say "Mark done" instead, and the
 *              supplier-owned ones offer no action at all: nobody in this
 *              console can close them, and a button that looked like they could
 *              would invite an admin to declare a cancellation settled that the
 *              airline has not answered.
 * @author      Kinetika Labs
 * @created     2026-09-24
 * @changelog
 *   - 2026-09-25 Claude — Added group_request (Query Management → Group Booking
 *                          Requests, the storefront's Flight Request form).
 *   - 2026-09-24 Claude — Initial.
 */

const corp = (row) => row.customerType === 'corporate';

export const ACTION_TARGETS = {
  flight_refund: {
    icon: 'refund',
    tone: 'violet',
    what: 'Refund',
    step: 'Work the refund',
    // ?open=<id> lifts the booking's own details drawer, which is where the
    // refund pipeline lives — landing on the list with 400 rows and a reference
    // to hunt for is not a link, it is a hint.
    href: (row) => `${corp(row) ? '/flight/corporate-bookings' : '/flight/bookings'}?open=${row.id}`,
  },
  hotel_refund: {
    icon: 'refund',
    tone: 'violet',
    what: 'Refund',
    step: 'Work the refund',
    href: (row) => `${corp(row) ? '/hotel/corporate-bookings' : '/hotel/bookings'}?open=${row.id}`,
  },
  insurance_refund: {
    icon: 'refund',
    tone: 'violet',
    what: 'Refund',
    step: 'Work the refund',
    href: (row) => `${corp(row) ? '/insurance/corporate-bookings' : '/insurance/bookings'}`,
  },
  flight_cancellation: {
    icon: 'clock',
    tone: 'amber',
    what: 'Cancellation',
    step: 'Track',
    href: (row) => `${corp(row) ? '/flight/corporate-cancellations' : '/flight/cancellations'}?q=${encodeURIComponent(row.reference || '')}`,
  },
  hotel_cancellation: {
    icon: 'clock',
    tone: 'amber',
    what: 'Cancellation',
    step: 'Track',
    href: (row) => `${corp(row) ? '/hotel/corporate-cancellations' : '/hotel/cancellations'}?q=${encodeURIComponent(row.reference || '')}`,
  },
  policy_request: {
    icon: 'badge',
    tone: 'teal',
    what: 'Policy request',
    step: 'Upload policy',
    href: (row) => (corp(row) ? '/insurance/corporate-policy-requests' : '/insurance/policy-requests'),
  },
  corporate_request: {
    icon: 'users',
    tone: 'teal',
    what: 'New company',
    step: 'Approve or decline',
    href: () => '/account/corporate-requests',
  },
  credit_due: {
    icon: 'card',
    tone: 'amber',
    what: 'Credit due',
    step: 'Settle due',
    href: () => '/account/credit-dues',
  },
  payment_reconciliation: {
    icon: 'wallet',
    tone: 'rose',
    what: 'Discrepancy',
    step: 'Mark done',
    href: () => '/account/payment-reconciliation',
  },
  abandoned_payment: {
    icon: 'bell',
    tone: 'amber',
    what: 'Abandoned checkout',
    step: 'Record the call',
    href: () => '/account/abandoned-payments',
  },
  enquiry: {
    icon: 'mail',
    tone: 'teal',
    what: 'Enquiry',
    step: 'Mark done',
    href: () => '/queries',
  },
  group_request: {
    icon: 'users',
    tone: 'teal',
    what: 'Group request',
    // NOT "Mark done": a quote request is finished by quoting it and learning
    // whether they booked, which is a judgement and an outcome — won and lost
    // are different answers. The API refuses it as NEEDS_DECISION for the same
    // reason, so the button says what the next real step is.
    step: 'Quote the trip',
    href: (row) => `/queries/group-requests?open=${row.id}`,
  },
};

/** Safe lookup — an unknown kind renders as itself rather than blanking a row. */
export function targetFor(kind) {
  return ACTION_TARGETS[kind] || { icon: 'page', tone: 'teal', what: kind, step: 'Open', href: () => '/' };
}

export const hrefFor = (row) => targetFor(row.kind).href(row);

/**
 * What the row's button should say. `resolvable` comes from the API (the queues
 * with one terminal step); `owner` is who the item is waiting on. Both are the
 * server's answer — the UI never decides for itself that something can be
 * closed.
 */
export function stepLabel(row) {
  if (row.owner === 'supplier') return 'Awaiting supplier';
  return targetFor(row.kind).step;
}
